/**
 * Правила тексту, спільні для сервера й браузера: нормалізація значення `text` / `long_text` і
 * два способи рахувати довжину. Окремий підшлях `@clientline/contracts/text-rules` без zod і
 * `re2js` (як `./i18n`): його беруть DAL і веб-форми, тож «що збереже сервер» і «що перевірить
 * клієнт» не можуть розійтися (Should 1 Архітектора, N1 Frontend-2, #361). Імпортів у файлі немає
 * навмисно: у перший екран він не тягне нічого зайвого.
 *
 * Не плутати з правилом для текстів метаданих (підписи, значення списків, форми слів): воно суворіше
 * й окреме — `hasForbiddenTextChars` у `safe-text.ts` (разом із символами напрямку письма U+202A…202E
 * і U+2066…2069, яких тут немає).
 */

/**
 * Керівні символи (FLD-02): C0, DEL, C1, U+2028 / U+2029, крім табуляції й переносу рядка. Усі
 * вони з BMP, тож перевірка по кодових одиницях збігається з перевіркою по символах.
 */
// biome-ignore lint/suspicious/noControlCharactersInRegex: керівні символи — предмет правила
const CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u2028\u2029]/;
const CONTROL_ALL = new RegExp(CONTROL.source, 'g');

/** `true`, якщо в тексті є керівний символ. */
export const hasControlChars = (text: string): boolean => CONTROL.test(text);

/** Текст без керівних символів. */
export const stripControlChars = (text: string): string => text.replace(CONTROL_ALL, '');

/**
 * Переноси рядка: `\r\n`, `\r` і Unicode-розриви, крім `\n`: NEL (U+0085), розділювачі рядка й
 * абзацу (U+2028, U+2029). Це переноси, а не сміття (nit Архітектора #199): у рядку — пробіл, у
 * довгому тексті — `\n`. Вирізати їх означало б склеїти слова («Київ\u2028Львів» → «КиївЛьвів»).
 */
const BREAKS = /\r\n?|[\u0085\u2028\u2029]/g;

/** Рядок (`text`, FLD-02): переноси й табуляція — пробіли, керівні символи геть, краї обрізано. */
export function normalizeSingleLine(raw: string): string {
  return stripControlChars(raw.replace(BREAKS, ' ').replace(/[\n\t]/g, ' ')).trim();
}

/** Довгий текст (`long_text`): переноси — `\n`, керівні символи геть; лише пробіли — порожньо. */
export function normalizeMultiLine(raw: string): string {
  const text = stripControlChars(raw.replace(BREAKS, '\n'));
  return text.trim() === '' ? '' : text;
}

/**
 * Довжина тексту в символах Unicode (кодових точках). Так рахує все, що вводить людина: значення
 * поля (DAL, `normalizeValue`: `maxLength` типу `text` / `long_text` / `picklist`) і тексти
 * метаданих (підпис, підказка, опис, повідомлення шаблону): `z.string().max()` у zod 4.6 теж
 * міряє кодовими точками, а не одиницями UTF-16. Це властивість версії zod, тож її закріплює тест
 * `text-rules.test.ts`: оновлення, що її змінить, не пройде CI, а не розведе клієнт із сервером.
 * Правило `maxLength` у `apps/web/src/ui/form/rules.ts` рахує те саме на місці (імпорт цього модуля
 * в групу `shared` роздув би кожен лінивий чанк), а `platform/pattern.test.ts` тримає обидва разом.
 */
export const codePointLength = (text: string): number => [...text].length;

/** Апострофи, які пошук не розрізняє й прибирає (SRC-06): `'` `’` `ʼ` `‘` `ʹ` `` ` `` `´` `′`. */
const SEARCH_APOSTROPHES_ALL = /['’ʼ‘ʹ`´′]/gu;
/** Усе, що не літера й не десяткова цифра, — роздільник слів (`[^[:alnum:]]` ICU у SQL). */
const SEARCH_NON_WORD_ALL = /[^\p{L}\p{Nd}]+/gu;

/**
 * Нормалізація пошуку (SRC-06; S3, розд. 2.2) — двійник SQL `search.norm` (міграція 0128,
 * `packages/data`), рівність тримає інтеграційний тест на справжньому PostgreSQL: апострофи геть
 * (до NFKC — бо NFKC розкладає `´` на пробіл і U+0301, — і після нього), NFKC, нижній регістр,
 * `ё` = `е`, усе, що не літера й не цифра, — один пробіл, краї обрізано. «Мар’яна», «марʼяна» й
 * «МАР'ЯНА» дають `маряна`. Лише для зіставлення людиною (глобальний пошук, далі `starts_with`
 * фільтрів); тотожність значень (`eq`, унікальність, теги) — `crm.norm`, бо там розділові знаки
 * значущі (`Web-1410` ≠ `Web 1410`; s3-search.md, розд. 8, п. 2).
 */
export function searchNorm(raw: string): string {
  return raw
    .replace(SEARCH_APOSTROPHES_ALL, '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(SEARCH_APOSTROPHES_ALL, '')
    .replace(/ё/gu, 'е')
    .replace(SEARCH_NON_WORD_ALL, ' ')
    .trim();
}

const SEARCH_SKEL_DROP_ALL = /[ьъ]/gu;
const SEARCH_SKEL_I_ALL = /[їіиый]/gu;
const SEARCH_SKEL_E_ALL = /[єэ]/gu;

/**
 * Кістяк слова для нечіткого збігу (SRC-03; S3, розд. 2.2) — двійник SQL `search.skel` (міграція
 * 0135, `packages/data`), рівність тримає `packages/data/src/search/lexicon.int.test.ts`: `ь` `ъ`
 * геть, `ї` `і` `и` `ы` `й` = `и`, `є` `э` = `е`, `ґ` = `г`. Вхід — слово після `searchNorm` (нижній
 * регістр): «Ірина» і «Ирина» дають `ирина`, «Марьяна» і «Маряна» — `маряна`. Решту символів не чіпає.
 */
export function searchSkel(word: string): string {
  return word
    .replace(SEARCH_SKEL_DROP_ALL, '')
    .replace(SEARCH_SKEL_I_ALL, 'и')
    .replace(SEARCH_SKEL_E_ALL, 'е')
    .replace(/ґ/gu, 'г');
}

/** Слова запиту чи значення після `searchNorm`; порожній текст — порожній перелік. */
export function searchWords(raw: string): string[] {
  const text = searchNorm(raw);
  return text === '' ? [] : text.split(' ');
}

/** Найкоротше слово запиту пошуку (ADR-0010, розд. 4, п. 3; related-search-api.md, розд. 1). */
export const SEARCH_WORD_MIN = 3;

/** Найдовший текст пошуку: глобального й у поданні. */
export const SEARCH_QUERY_MAX = 100;

/** Символи напрямку, які приносить вставка номера з контактів. */
const BIDI_MARKS = /[\u202a-\u202e\u2066-\u2069]/gu;
/** Табуляція й переноси, зокрема NEL, U+2028 і U+2029: запис значень теж робить із них пробіл. */
const SEARCH_SPACES = /[\t\n\v\f\r\u0085\u2028\u2029]/gu;

/**
 * Текст пошуку так, як його побачить сервер: без символів напрямку, переноси й табуляція — пробіл,
 * без пробілів на краях. Те саме очищення на клієнті й у схемі: інакше вставлений номер не впізнати.
 * Решту керівних символів, зокрема NUL, схема відхиляє.
 */
export function cleanSearchText(raw: string): string {
  return raw.replace(BIDI_MARKS, '').replace(SEARCH_SPACES, ' ').trim();
}

/**
 * Запит закороткий для пошуку (`422 search_query_too_short`): серед слів `searchWords` (двійник SQL
 * `search.norm`, та сама норма, що в DAL пошуку E5-01) немає жодного від `SEARCH_WORD_MIN` кодових
 * точок. Апостроф слова не ділить ні до NFKC, ні після нього («з＇їв» → `зїв`). DAL вирішує за тим
 * самим правилом (`searchTextQuery`, рівність тримає `packages/data/src/search/query.test.ts`).
 * Тут, а не в корені пакета, бо корінь вебу заборонений (zod), а палітра такого запиту не шле.
 */
export function searchQueryTooShort(q: string): boolean {
  return !searchWords(q).some((word) => codePointLength(word) >= SEARCH_WORD_MIN);
}
